import { useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Brain } from "lucide-react";
import { fmtUsd, CHAIN_META } from "@/lib/format";

const MOOD_LABEL = { "🚀": "Conviction", "😬": "FOMO", "🎰": "Gamble", "🧠": "Research", "💎": "Diamond", "😴": "Boring" };
const fmtH = (h) => (h == null ? "—" : h < 24 ? `${Math.round(h)}h` : `${(h / 24).toFixed(1)}d`);

function groupStats(trades, keyFn) {
  const g = {};
  for (const t of trades) {
    if (t.status !== "closed") continue;
    const k = keyFn(t); if (!k) continue;
    (g[k] ||= { n: 0, wins: 0, pnl: 0 });
    g[k].n++; g[k].pnl += t.pnl; if (t.pnl > 0) g[k].wins++;
  }
  return Object.entries(g).map(([k, v]) => ({ k, ...v, winRate: Math.round((v.wins / v.n) * 100) })).sort((a, b) => b.pnl - a.pnl);
}

function Bars({ title, rows, labelFn, colorFn, testId }) {
  const max = Math.max(1, ...rows.map((r) => Math.abs(r.pnl)));
  return (
    <div data-testid={testId}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">{title}</div>
      {rows.length === 0 && <div className="text-xs text-slate-600">Close a few trades to see this.</div>}
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.k} className="flex items-center gap-3 text-xs">
            <span className="w-24 truncate" style={{ color: colorFn?.(r.k) }}>{labelFn(r.k)}</span>
            <div className="flex-1 h-1.5 rounded-full bg-[#0c111b] overflow-hidden">
              <div className={`h-full rounded-full ${r.pnl >= 0 ? "bg-emerald-500" : "bg-red-500"}`} style={{ width: `${(Math.abs(r.pnl) / max) * 100}%` }} />
            </div>
            <span className={`w-16 text-right font-mono ${r.pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>{fmtUsd(r.pnl)}</span>
            <span className="w-14 text-right font-mono text-slate-400">{r.winRate}% · {r.n}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PatternInsights({ trades }) {
  const s = useMemo(() => {
    const closed = trades.filter((t) => t.status === "closed");
    const wins = closed.filter((t) => t.pnl > 0), losses = closed.filter((t) => t.pnl < 0);
    const avg = (arr, f) => (arr.length ? arr.reduce((a, t) => a + f(t), 0) / arr.length : null);
    const grossW = wins.reduce((a, t) => a + t.pnl, 0), grossL = Math.abs(losses.reduce((a, t) => a + t.pnl, 0));
    return {
      closed: closed.length,
      winRate: closed.length ? Math.round((wins.length / closed.length) * 100) : null,
      avgWin: avg(wins, (t) => t.pnl), avgLoss: avg(losses, (t) => t.pnl),
      profitFactor: grossL > 0 ? grossW / grossL : grossW > 0 ? Infinity : null,
      holdWin: avg(wins.filter((t) => t.hoursHeld != null), (t) => t.hoursHeld),
      holdLoss: avg(losses.filter((t) => t.hoursHeld != null), (t) => t.hoursHeld),
      avgSize: avg(closed, (t) => t.amount),
      byMood: groupStats(trades, (t) => t.mood), byChain: groupStats(trades, (t) => t.chain),
    };
  }, [trades]);

  const pf = s.profitFactor == null ? "—" : s.profitFactor === Infinity ? "∞" : s.profitFactor.toFixed(2);
  const tips = [];
  if (s.closed >= 3) {
    if (s.avgLoss != null && s.avgWin != null && Math.abs(s.avgLoss) > s.avgWin) tips.push("Your average loss is bigger than your average win — cut losers sooner or let winners run.");
    if (s.holdLoss != null && s.holdWin != null && s.holdLoss > s.holdWin * 1.5) tips.push("You hold losers much longer than winners. Set an exit plan before you ape.");
    const worstMood = [...s.byMood].sort((a, b) => a.pnl - b.pnl)[0];
    if (worstMood && worstMood.pnl < 0 && worstMood.n >= 2) tips.push(`"${MOOD_LABEL[worstMood.k] || worstMood.k}" trades are your biggest leak (${fmtUsd(worstMood.pnl)} across ${worstMood.n}).`);
    if (s.profitFactor != null && s.profitFactor !== Infinity && s.profitFactor >= 1.5) tips.push(`Profit factor ${pf} — you make $${pf} for every $1 lost. Keep doing what works.`);
  }

  return (
    <Card className="bg-[#111723] border-[#222F43] p-5 sm:p-6" data-testid="pattern-insights">
      <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-4 flex items-center gap-2"><Brain className="h-4 w-4 text-purple-400" /> Patterns</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {[
          ["Win rate", s.winRate == null ? "—" : `${s.winRate}%`, s.winRate == null ? "" : s.winRate >= 50 ? "text-emerald-400" : "text-amber-400"],
          ["Avg win", fmtUsd(s.avgWin), "text-emerald-400"],
          ["Avg loss", fmtUsd(s.avgLoss), "text-red-400"],
          ["Profit factor", pf, s.profitFactor >= 1 ? "text-emerald-400" : s.profitFactor == null ? "" : "text-red-400"],
          ["Hold · wins", fmtH(s.holdWin), "text-slate-200"],
          ["Hold · losses", fmtH(s.holdLoss), "text-slate-200"],
        ].map(([l, v, c]) => (
          <div key={l} className="rounded-lg bg-[#0c111b] border border-[#222F43] p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{l}</div>
            <div className={`font-mono font-bold text-lg mt-0.5 ${c}`}>{v}</div>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <Bars title="P&L by mood" rows={s.byMood} labelFn={(k) => `${k} ${MOOD_LABEL[k] || ""}`} testId="pnl-by-mood" />
        <Bars title="P&L by chain" rows={s.byChain} labelFn={(k) => CHAIN_META[k]?.label || k} colorFn={(k) => CHAIN_META[k]?.color} testId="pnl-by-chain" />
      </div>
      {tips.length > 0 && (
        <div className="mt-6 space-y-1.5" data-testid="pattern-tips">
          {tips.map((t, i) => <div key={i} className="text-sm text-slate-300 flex gap-2"><span className="text-purple-400">›</span>{t}</div>)}
        </div>
      )}
    </Card>
  );
}
